<template>
  <div>
    <label class="block text-sm font-medium text-[#22282F] mb-1">Circuits selon le profil du demandeur</label>
    <p class="text-xs text-gray-400 mb-3">
      Les règles se lisent dans l'ordre : la première qui correspond au rédacteur choisit le workflow.
      Si aucune ne correspond, c'est le workflow de validation ci-dessus.
    </p>

    <div class="space-y-3">
      <div
        v-for="(regle, idx) in modelValue"
        :key="idx"
        class="border border-gray-200 rounded-lg p-3 bg-gray-50/60"
        data-test="regle"
      >
        <div class="flex items-center justify-between mb-2">
          <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Règle {{ idx + 1 }}</span>
          <div class="flex items-center gap-0.5">
            <button
              type="button"
              :disabled="idx === 0"
              class="p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30"
              title="Monter"
              @click="deplacer(idx, -1)"
            >
              <ArrowUp class="w-4 h-4" />
            </button>
            <button
              type="button"
              :disabled="idx === modelValue.length - 1"
              class="p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30"
              title="Descendre"
              @click="deplacer(idx, 1)"
            >
              <ArrowDown class="w-4 h-4" />
            </button>
            <button
              type="button"
              class="p-1 text-red-500 hover:text-red-600"
              title="Retirer la règle"
              data-test="retirer-regle"
              @click="retirer(idx)"
            >
              <Trash2 class="w-4 h-4" />
            </button>
          </div>
        </div>

        <!-- Rôles -->
        <div class="mb-2">
          <span class="block text-xs text-gray-500 mb-1">Rôles</span>
          <div class="flex flex-wrap items-center gap-1.5">
            <span
              v-for="roleId in regle.role_ids"
              :key="roleId"
              class="inline-flex items-center gap-1 bg-white border border-gray-200 text-gray-700 text-xs rounded-md pl-2 pr-1 py-0.5"
              data-test="role"
            >
              {{ nomDuRole(roleId) }}
              <button
                type="button"
                class="p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700"
                :aria-label="`Retirer ${nomDuRole(roleId)}`"
                @click="retirerRole(idx, roleId)"
              >
                <X class="w-3 h-3" />
              </button>
            </span>
            <select
              class="px-2 py-1 border border-gray-200 rounded-md text-xs bg-white focus:outline-none focus:border-[#22282F]"
              data-test="ajout-role"
              @change="ajouterRole(idx, $event)"
            >
              <option value="">+ Ajouter un rôle</option>
              <option v-for="role in rolesDisponibles(regle)" :key="role.id" :value="String(role.id)">
                {{ role.nom }} ({{ role.code }})
              </option>
            </select>
          </div>
        </div>

        <!-- Direction et workflow -->
        <div class="grid grid-cols-2 gap-2">
          <div>
            <span class="block text-xs text-gray-500 mb-1">Direction</span>
            <select
              :value="regle.direction_id"
              class="w-full px-2 py-1.5 border border-gray-200 rounded-md text-xs bg-white focus:outline-none focus:border-[#22282F]"
              data-test="direction"
              @change="modifier(idx, { direction_id: ($event.target as HTMLSelectElement).value })"
            >
              <option value="">Toutes les directions</option>
              <option v-for="d in directions" :key="d.id" :value="String(d.id)">{{ d.code }} — {{ d.nom }}</option>
            </select>
          </div>
          <div>
            <span class="block text-xs text-gray-500 mb-1">Workflow</span>
            <select
              :value="regle.workflow_template_id"
              class="w-full px-2 py-1.5 border border-gray-200 rounded-md text-xs bg-white focus:outline-none focus:border-[#22282F]"
              data-test="workflow"
              @change="modifier(idx, { workflow_template_id: ($event.target as HTMLSelectElement).value })"
            >
              <option value="">-- Choisir un workflow --</option>
              <option v-for="wt in workflowsProposes(regle)" :key="wt.id" :value="String(wt.id)">
                {{ wt.nom }}{{ wt.is_active ? '' : ' (désactivé)' }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <button
      type="button"
      class="mt-3 inline-flex items-center gap-1.5 text-sm text-[#22282F] hover:underline"
      data-test="ajouter-regle"
      @click="ajouter"
    >
      <Plus class="w-4 h-4" /> Ajouter une règle
    </button>
  </div>
</template>

<script setup lang="ts">
import { ArrowDown, ArrowUp, Plus, Trash2, X } from 'lucide-vue-next'
import type { RegleBrouillon } from '@/types/workflowTemplate'

interface RoleOption { id: number; code: string; nom: string }
interface DirectionOption { id: number; code: string; nom: string }
interface WorkflowOption { id: number; nom: string; is_active: boolean }

const props = defineProps<{
  modelValue: RegleBrouillon[]
  roles: RoleOption[]
  directions: DirectionOption[]
  workflowTemplates: WorkflowOption[]
}>()

const emit = defineEmits<{ (e: 'update:modelValue', value: RegleBrouillon[]): void }>()

const publier = (regles: RegleBrouillon[]) => emit('update:modelValue', regles)

const modifier = (idx: number, champs: Partial<RegleBrouillon>) => {
  publier(props.modelValue.map((r, i) => (i === idx ? { ...r, ...champs } : r)))
}

const ajouter = () => {
  publier([...props.modelValue, { role_ids: [], direction_id: '', workflow_template_id: '' }])
}

const retirer = (idx: number) => {
  publier(props.modelValue.filter((_, i) => i !== idx))
}

const deplacer = (idx: number, sens: -1 | 1) => {
  const cible = idx + sens
  if (cible < 0 || cible >= props.modelValue.length) return
  const regles = [...props.modelValue]
  ;[regles[idx], regles[cible]] = [regles[cible], regles[idx]]
  publier(regles)
}

const ajouterRole = (idx: number, event: Event) => {
  const select = event.target as HTMLSelectElement
  const roleId = Number(select.value)
  select.value = ''
  if (!roleId) return
  const regle = props.modelValue[idx]
  if (regle.role_ids.includes(roleId)) return
  modifier(idx, { role_ids: [...regle.role_ids, roleId] })
}

const retirerRole = (idx: number, roleId: number) => {
  modifier(idx, { role_ids: props.modelValue[idx].role_ids.filter(id => id !== roleId) })
}

const nomDuRole = (roleId: number) => props.roles.find(r => r.id === roleId)?.nom ?? `Rôle #${roleId}`

const rolesDisponibles = (regle: RegleBrouillon) => props.roles.filter(r => !regle.role_ids.includes(r.id))

// Workflows actifs, plus celui déjà choisi s'il a été désactivé depuis (pour ne pas
// le faire disparaître du formulaire sans prévenir).
const workflowsProposes = (regle: RegleBrouillon) =>
  props.workflowTemplates.filter(wt => wt.is_active || String(wt.id) === regle.workflow_template_id)
</script>
